// src/pages/inventory/settings/SupplierManagement.tsx
import React, { useState, useEffect } from "react";
import { Table, Button, Space, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import { fetchSuppliers, Supplier } from "../../../api/inventory/supplierApi";
import { PageToolbar, SectionCard } from "../../../components/shared";
import EditSupplierDrawer from "./suppliers/EditSupplierDrawer";
import { useNotifications } from "../../../context/NotificationContext";
import { loadUserPreferences } from "../../../utils/userPrefs/UserPrefs";

const { Text } = Typography;

const defaultSupplier: Supplier = {
	name: "",
	contact_name: "",
	email: "",
	phone: "",
	address: "",
};

const SupplierManagement: React.FC = () => {
	const [suppliers, setSuppliers] = useState<Supplier[]>([]);
	const [totalSuppliers, setTotalSuppliers] = useState<number>(0);
	const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(false);
	const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
	const { showError, showSuccess } = useNotifications();
	const SUPPLIERS_PER_PAGE = loadUserPreferences().tableRowsPerPage;

	useEffect(() => {
		loadSuppliers();
	}, [currentPage]);

	const loadSuppliers = async () => {
		setLoading(true);
		try {
			const { data, total } = await fetchSuppliers(currentPage, SUPPLIERS_PER_PAGE);
			setSuppliers(data);
			setTotalSuppliers(total);
		} catch (error) {
			console.error("Error loading suppliers:", error);
			showError("Failed to load suppliers");
		} finally {
			setLoading(false);
		}
	};

	const handleAdd = () => {
		setSelectedSupplier(defaultSupplier);
		setDrawerOpen(true);
	};

	const handleEdit = (supplier: Supplier) => {
		setSelectedSupplier(supplier);
		setDrawerOpen(true);
	};

	const handleDrawerClose = () => {
		setSelectedSupplier(null);
		setDrawerOpen(false);
		loadSuppliers();
	};

	const columns: ColumnsType<Supplier> = [
		{
			title: "Supplier Name",
			dataIndex: "name",
			key: "name",
			render: (text: string) => <Text strong>{text}</Text>,
		},
		{
			title: "Contact Name",
			dataIndex: "contact_name",
			key: "contact_name",
			render: (text: string) => text || "—",
		},
		{
			title: "Email",
			dataIndex: "email",
			key: "email",
			render: (text: string) => text || "—",
		},
		{
			title: "Phone",
			dataIndex: "phone",
			key: "phone",
			render: (text: string) => text || "—",
		},
		{
			title: "Address",
			dataIndex: "address",
			key: "address",
			ellipsis: true,
			render: (text: string) => <Text type="secondary">{text || "—"}</Text>,
		},
		{
			title: "Actions",
			key: "actions",
			width: 100,
			render: (_: any, record: Supplier) => (
				<Space size="small">
					<Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
				</Space>
			),
		},
	];

	return (
		<>
			<PageToolbar
				primaryAction={{
					label: "Add Supplier",
					icon: <PlusOutlined />,
					onClick: handleAdd,
				}}
				filters={[
					{
						type: "search",
						key: "search",
						placeholder: "Search suppliers...",
						value: searchTerm,
						onChange: setSearchTerm,
						width: 250,
					},
				]}
				onReset={() => {
					setSearchTerm("");
					setCurrentPage(1);
				}}
			/>

			<SectionCard>
				<Table
					columns={columns}
					dataSource={suppliers}
					rowKey="supplier_id"
					loading={loading}
					size="middle"
					pagination={{
						current: currentPage,
						pageSize: SUPPLIERS_PER_PAGE,
						total: totalSuppliers,
						onChange: setCurrentPage,
						showTotal: (total) => `${total} suppliers`,
					}}
				/>
			</SectionCard>

			{selectedSupplier && <EditSupplierDrawer isOpen={drawerOpen} onClose={handleDrawerClose} supplier={selectedSupplier} />}
		</>
	);
};

export default SupplierManagement;
